// Pure helpers for the Platforms page (goals/admin-panel/DESIGN.md "Features
// and Platforms pages"): latest splits with shares, version ordering, the
// top versions as chart series, registration shares and push success rates.
import type { AnalyticsSeries } from "./analytics";
import { latest } from "./features";
import { seriesFor } from "./growth";

/**
 * The key of the read's overflow series (`other: true`, the smallest values
 * past its series cap), kept apart from the rollup's own `other` (client text
 * that is not version-shaped).
 */
export const OVERFLOW = "+more";

export interface Split {
  key: string;
  value: number;
  /** 0–1 of the split's total (null when the total is 0). */
  share: number | null;
}

/**
 * Each series' value in the latest bucket where any is known, grouped by
 * `dim`, with its share of that bucket's total; null without such a bucket.
 */
export function latestSplit(
  read: AnalyticsSeries,
  dim: string,
): { bucket: string; total: number; parts: Split[] } | null {
  let index = -1;
  for (let i = read.buckets.length - 1; i >= 0 && index < 0; i--)
    if (read.built?.[i] || read.series.some((s) => s.values[i] !== null && s.values[i] !== undefined)) index = i;
  if (index < 0) return null;
  const parts = read.series.map((s) => ({
    key: s.other ? OVERFLOW : (s.dims?.[dim] ?? ""),
    value: s.values[index] ?? 0,
  }));
  const sum = parts.reduce((a, p) => a + p.value, 0);
  return {
    bucket: read.buckets[index]!,
    total: sum,
    parts: parts.map((p) => ({ ...p, share: sum > 0 ? p.value / sum : null })),
  };
}

/**
 * Releases newest first by their numeric parts ("1.10.0" before "1.9.2");
 * the overflow, `other`, then `unknown`, last.
 */
export function compareVersions(a: string, b: string): number {
  const rank = (v: string) => (v === "unknown" ? 3 : v === "other" ? 2 : v === OVERFLOW ? 1 : 0);
  if (rank(a) || rank(b)) return rank(a) - rank(b) || a.localeCompare(b);
  const [pa, pb] = [a, b].map((v) => v.split(/[.-]/).map((part) => (/^\d+$/.test(part) ? Number(part) : part)));
  for (let i = 0; i < Math.max(pa!.length, pb!.length); i++) {
    const [x, y] = [pa![i], pb![i]];
    if (x === y) continue;
    // A release ("1.1.0") sorts before its pre-releases ("1.1.0-beta1").
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (typeof x === "number" && typeof y === "number") return y - x;
    if (typeof x === "number") return -1;
    if (typeof y === "number") return 1;
    return x.localeCompare(y);
  }
  return 0;
}

/** Parts sorted as versions (`compareVersions`). */
export const byVersion = (parts: readonly Split[]) => [...parts].sort((a, b) => compareVersions(a.key, b.key));

export interface NamedSeries {
  key: string;
  values: (number | null)[];
}

/**
 * The `count` largest values of `dim` in the latest bucket as their own
 * series (newest version first), every other value summed into `rest`
 * (absent when nothing is left over).
 */
export function topSeries(
  read: AnalyticsSeries,
  dim: string,
  count: number,
): { top: NamedSeries[]; rest: (number | null)[] | null } {
  const split = latestSplit(read, dim);
  const keys = split
    ? [...split.parts]
        .filter((p) => p.key !== OVERFLOW && p.key !== "other")
        .sort((a, b) => b.value - a.value || compareVersions(a.key, b.key))
        .slice(0, count)
        .map((p) => p.key)
        .sort(compareVersions)
    : [];
  const top = keys.map((key) => ({ key, values: seriesFor(read, dim, key) }));
  const others = read.series.filter((s) => s.other || !keys.includes(s.dims?.[dim] ?? ""));
  if (!others.length) return { top, rest: null };
  const rest = read.buckets.map((_, i) => {
    let sum: number | null = null;
    for (const s of others) {
      const v = s.values[i];
      if (v !== null && v !== undefined) sum = (sum ?? 0) + v;
    }
    return sum;
  });
  return { top, rest };
}

/**
 * Per bucket, the share (percent, 0–100, one decimal) of the rows `match`
 * keeps that `yes` also keeps; null where none were counted.
 */
export function sharePercent(
  read: AnalyticsSeries,
  match: (dims: Record<string, string>) => boolean,
  yes: (dims: Record<string, string>) => boolean,
): (number | null)[] {
  return read.buckets.map((_, i) => {
    let all = 0;
    let hit = 0;
    let built = false;
    for (const s of read.series) {
      if (!s.dims || !match(s.dims)) continue;
      const v = s.values[i];
      if (v === null || v === undefined) continue;
      built = true;
      all += v;
      if (yes(s.dims)) hit += v;
    }
    return built && all > 0 ? Math.round((hit / all) * 1000) / 10 : null;
  });
}

/** Per-bucket totals of the rows `match` keeps (null where nothing was built). */
export function sumWhere(read: AnalyticsSeries, match: (dims: Record<string, string>) => boolean): (number | null)[] {
  return read.buckets.map((_, i) => {
    let sum: number | null = null;
    for (const s of read.series) {
      if (!s.dims || !match(s.dims)) continue;
      const v = s.values[i];
      if (v !== null && v !== undefined) sum = (sum ?? 0) + v;
    }
    return sum;
  });
}

/** The latest known value of `values` (null without one). */
export const latestValue = (values: readonly (number | null)[]) => latest(values)?.value ?? null;

/** Push results as the dispatcher counts them, in display order. */
export const PUSH_RESULTS = ["sent", "invalid", "failed", "unavailable"] as const;
export const PUSH_CHANNELS = [
  { id: "fcm", label: "Firebase (Android)" },
  { id: "webpush", label: "Web Push" },
] as const;
